import { useState } from "react";
import { AlertTriangle, Check, History, Sparkles, Undo2, X } from "lucide-react";

import {
  useAnalysePolicy,
  useApplyRecommendation,
  useApproveRecommendation,
  usePolicyHistory,
  usePolicyRecommendations,
  useRejectRecommendation,
  useRevertPolicy,
} from "@/api/queries";
import type { InventoryPolicy, PolicyChange, PolicyRecommendation } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { EmptyState, Skeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { ConfidenceIndicator } from "@/features/ingestion/ConfidenceIndicator";
import {
  formatDateTime,
  formatPolicyChangeSource,
  formatPolicyMode,
  formatPolicyReason,
  formatRecommendationStatus,
  formatThreshold,
} from "@/lib/format";
import { recommendationTone } from "@/lib/stock";

/**
 * Une politique : ce qu'elle vaut, ce qu'on lui propose, et son histoire.
 *
 * Rien ici ne parle d'achat. « Appliquer » écrit des seuils — c'est tout ce que
 * ça fait, et le panneau le dit pour qu'aucune formulation ne laisse croire
 * qu'une commande vient de partir.
 */
export function PolicyDetailPanel({
  policy,
  onClose,
}: {
  policy: InventoryPolicy | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const analyse = useAnalysePolicy();
  const { data: recommendations, isLoading } = usePolicyRecommendations(policy?.id);
  const { data: history } = usePolicyHistory(policy?.id);

  if (!policy) return null;

  const items = recommendations?.items ?? [];
  const pending = items.find((item) => item.status === "PENDING" || item.status === "APPROVED");
  const past = items.filter((item) => item !== pending);

  async function run() {
    if (!policy) return;
    try {
      const result = await analyse.mutateAsync({ policyId: policy.id });
      toast.success(
        "Analyse terminée",
        result.items.length > 0
          ? "Une suggestion est à vérifier."
          : "Ces seuils tiennent la route ; rien à changer.",
      );
    } catch (error) {
      toast.error("Analyse impossible", String(error));
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={policy.product_name ?? "Politique"}
      description={`${policy.location_name ?? "Partout"} · ${formatPolicyMode(policy.mode ?? "MANUAL")}`}
      className="max-w-3xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Fermer
          </Button>
          <Button variant="primary" onClick={() => void run()} loading={analyse.isPending}>
            <Sparkles className="size-4" aria-hidden />
            Analyser
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Current policy={policy} />

        {isLoading && <Skeleton className="h-24" />}

        {!isLoading && !pending && (
          <EmptyState
            title="Aucune suggestion en attente"
            description="Lancez l'analyse : consommation réelle, délai fournisseur, variabilité et demande confirmée. Aucun achat n'en découle."
          />
        )}

        {pending && <RecommendationCard policy={policy} recommendation={pending} />}

        {past.length > 0 && (
          <details className="rounded-lg border border-border">
            <summary className="cursor-pointer px-3 py-2 text-[13px] text-ink-muted">
              {past.length} suggestion{past.length > 1 ? "s" : ""} déjà décidée
              {past.length > 1 ? "s" : ""}
            </summary>
            <ul className="divide-y divide-border/60">
              {past.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-[12px]"
                >
                  <span className="truncate text-ink-muted">
                    MAX {formatThreshold(item.current_max)} →{" "}
                    {formatThreshold(item.proposed_max)}
                  </span>
                  <StatusBadge tone={recommendationTone(item.status)}>
                    {formatRecommendationStatus(item.status)}
                  </StatusBadge>
                </li>
              ))}
            </ul>
          </details>
        )}

        <HistoryList policyId={policy.id} changes={history?.items ?? []} />
      </div>
    </Dialog>
  );
}

function Current({ policy }: { policy: InventoryPolicy }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg border border-border bg-surface-sunken p-3 text-[13px] @2xl:grid-cols-5">
      {(
        [
          ["MIN", policy.min_quantity],
          ["MAX", policy.max_quantity],
          ["Pt commande", policy.reorder_point],
          ["Qté commande", policy.reorder_quantity],
          ["Sécurité", policy.safety_stock],
        ] as const
      ).map(([label, value]) => (
        <div key={label} className="flex flex-col">
          <dt className="text-[11px] text-ink-subtle">{label}</dt>
          <dd className="tnum text-ink">{formatThreshold(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function RecommendationCard({
  policy,
  recommendation,
}: {
  policy: InventoryPolicy;
  recommendation: PolicyRecommendation;
}) {
  const toast = useToast();
  const approve = useApproveRecommendation();
  const reject = useRejectRecommendation();
  const apply = useApplyRecommendation();
  const [reason, setReason] = useState("");

  const noProposal = recommendation.proposed_max === null;
  const ids = { policyId: policy.id, recommendationId: recommendation.id };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge tone={recommendationTone(recommendation.status)}>
            {formatRecommendationStatus(recommendation.status)}
          </StatusBadge>
          <ConfidenceIndicator level={recommendation.data_quality} />
          <span className="text-[11px] text-ink-subtle">
            fenêtre {recommendation.window_days} j · moteur {recommendation.engine_version}
          </span>
        </div>
      </div>

      {recommendation.is_stale && (
        <p className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-subtle px-3 py-2 text-[12px] text-ink">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
          La politique a changé depuis cette analyse. Relancez-la : appliquer
          écraserait une modification plus récente.
        </p>
      )}

      {!noProposal && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] @2xl:grid-cols-5">
          {(
            [
              ["MIN", recommendation.current_min, recommendation.proposed_min],
              ["MAX", recommendation.current_max, recommendation.proposed_max],
              [
                "Pt commande",
                recommendation.current_reorder_point,
                recommendation.proposed_reorder_point,
              ],
              [
                "Qté commande",
                recommendation.current_reorder_quantity,
                recommendation.proposed_reorder_quantity,
              ],
              [
                "Sécurité",
                recommendation.current_safety_stock,
                recommendation.proposed_safety_stock,
              ],
            ] as const
          ).map(([label, current, proposed]) => (
            <div key={label} className="flex flex-col">
              <dt className="text-[11px] text-ink-subtle">{label}</dt>
              <dd className="tnum text-ink">
                <span className="text-ink-muted line-through">{formatThreshold(current)}</span>{" "}
                → <span className="font-medium">{formatThreshold(proposed)}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      <ul className="flex flex-wrap gap-1.5">
        {recommendation.reason_codes.map((code) => (
          <li key={code}>
            <StatusBadge tone="info">{formatPolicyReason(code)}</StatusBadge>
          </li>
        ))}
      </ul>

      {/* L'explication vient du moteur déterministe, à partir des mêmes nombres
          que le calcul. Aucun modèle de langage ne l'écrit. */}
      <p className="text-[12px] text-ink-muted">{recommendation.explanation}</p>

      {recommendation.auto_blockers.length > 0 && recommendation.mode === "AUTO" && (
        <p className="text-[11px] text-warning">
          Application automatique bloquée :{" "}
          {recommendation.auto_blockers.map((code) => formatPolicyReason(code)).join(", ")}.
        </p>
      )}

      {noProposal ? (
        <p className="text-[12px] text-ink-subtle">
          Aucun seuil n'est proposé : les données ne permettent pas d'en défendre un.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {recommendation.status === "PENDING" && (
            <Button
              variant="secondary"
              size="sm"
              loading={approve.isPending}
              onClick={() =>
                approve.mutate(ids, {
                  onError: (error) => toast.error("Non approuvée", String(error)),
                })
              }
            >
              <Check className="size-4" aria-hidden />
              Approuver
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            disabled={recommendation.is_stale}
            loading={apply.isPending}
            onClick={() =>
              apply.mutate(ids, {
                onSuccess: () => toast.success("Seuils mis à jour", "Aucune commande n'a été créée."),
                onError: (error) => toast.error("Non appliquée", String(error)),
              })
            }
          >
            Appliquer les seuils
          </Button>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Raison du refus"
            aria-label="Raison du refus"
            className="h-9 min-w-0 flex-1 rounded-md border border-border bg-surface px-2 text-[13px]"
          />
          <Button
            variant="secondary"
            size="sm"
            loading={reject.isPending}
            onClick={() =>
              reject.mutate(
                { ...ids, reason },
                { onError: (error) => toast.error("Non refusée", String(error)) },
              )
            }
          >
            <X className="size-4" aria-hidden />
            Refuser
          </Button>
        </div>
      )}
    </div>
  );
}

/** L'historique, et le seul moyen de revenir en arrière : en ajoutant une ligne. */
function HistoryList({ policyId, changes }: { policyId: string; changes: PolicyChange[] }) {
  const toast = useToast();
  const revert = useRevertPolicy();

  if (changes.length === 0) return null;

  return (
    <details className="rounded-lg border border-border">
      <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-[13px] text-ink-muted">
        <History className="size-4" aria-hidden />
        Historique ({changes.length})
      </summary>
      <ul className="divide-y divide-border/60">
        {changes.map((change) => (
          <li key={change.id} className="flex items-start justify-between gap-3 px-3 py-2">
            <div className="min-w-0">
              <p className="text-[12px] text-ink">
                MIN {formatThreshold(change.old_min)} → {formatThreshold(change.new_min)} · MAX{" "}
                {formatThreshold(change.old_max)} → {formatThreshold(change.new_max)}
              </p>
              <p className="text-[11px] text-ink-subtle">
                {formatPolicyChangeSource(change.source)} · {change.actor} ·{" "}
                {formatDateTime(change.created_at)}
                {change.reverts_change_id ? " · annule un changement antérieur" : ""}
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              loading={revert.isPending}
              onClick={() =>
                revert.mutate(
                  { policyId, changeId: change.id },
                  {
                    onSuccess: () =>
                      toast.success("Seuils rétablis", "Une nouvelle ligne d'historique."),
                    onError: (error) => toast.error("Retour impossible", String(error)),
                  },
                )
              }
            >
              <Undo2 className="size-4" aria-hidden />
              Rétablir
            </Button>
          </li>
        ))}
      </ul>
    </details>
  );
}
